import { useLocation } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { For, Show } from "solid-js";
import type { QuickLookSource } from "../../enums/scan";
import { QUICK_LOOK_SOURCE } from "../../enums/scan";
import { LifecycleBadge, SeverityBadge, StatusBadge } from "../../ui/Badge";
import { pluralize } from "../../ui/format";
import { DrawerSection, DrawerShell } from "./DrawerShell";
import { Metric } from "./issueDetailParts";
import { currentPageReadableEvidenceForIssue } from "./issuePageEvidence";
import { uniqueCurrentPagesForIssue } from "./issuePageResolution";
import {
  clientIssueTitle,
  issueDimensionLabel,
  rationaleForIssueInSite,
  suggestedActionForIssueInSite,
} from "./issueText";

export function IssueQuickLookDrawer(props: {
  issue: Issue;
  websiteId: string;
  data: OverviewResponse;
  source?: QuickLookSource;
  onClose: () => void;
}) {
  const title = () => clientIssueTitle(props.issue);
  const evidence = () =>
    currentPageReadableEvidenceForIssue(props.data, props.issue).slice(0, 2);
  const affectedPages = () =>
    uniqueCurrentPagesForIssue(props.data, props.issue);
  const source = () => props.source ?? QUICK_LOOK_SOURCE.SUMMARY;
  const location = useLocation();
  const detailHref = () => {
    const params = new URLSearchParams();
    params.set("from", source());
    if (source() === QUICK_LOOK_SOURCE.ISSUES) {
      const returnParams = new URLSearchParams(location.search);
      returnParams.set("issue", props.issue.id);
      const returnQuery = returnParams.toString();
      if (returnQuery) {
        params.set("return", `?${returnQuery}`);
      }
    }
    return `/w/${props.websiteId}/scan/issues/${props.issue.id}?${params.toString()}`;
  };
  return (
    <DrawerShell
      kicker="Issue quick look"
      title={title()}
      ariaLabel={`Issue quick look: ${title()}`}
      onClose={props.onClose}
      footer={
        <a class="scan-btn scan-btn-primary" href={detailHref()}>
          View full issue
        </a>
      }
    >
      <div class="scan-drawer-badges">
        <SeverityBadge severity={props.issue.severity} />
        <StatusBadge status={props.issue.status} />
        <LifecycleBadge state={props.issue.lifecycleState} />
      </div>
      <DrawerSection title="Why it matters">
        <p>{rationaleForIssueInSite(props.data, props.issue)}</p>
      </DrawerSection>
      <div class="scan-drawer-metrics scan-issue-quick-look-metrics">
        <Metric
          label="Scale"
          value={`${affectedPages().length} unique ${pluralize(affectedPages().length, "page")}`}
        />
        <Metric label="Dimension" value={issueDimensionLabel(props.issue)} />
        <Metric label="Owner" value={props.issue.ownerName ?? "Unassigned"} />
      </div>
      <DrawerSection title="Suggested action">
        <p>{suggestedActionForIssueInSite(props.data, props.issue)}</p>
      </DrawerSection>
      <Show when={evidence().length > 0}>
        <DrawerSection title={`Evidence examples (${evidence().length})`}>
          <ul class="scan-evidence-list">
            <For each={evidence()}>
              {(item) => (
                <li class="scan-evidence-card">
                  <strong class="mono">{item.path}</strong>
                  <p>{item.value}</p>
                </li>
              )}
            </For>
          </ul>
        </DrawerSection>
      </Show>
    </DrawerShell>
  );
}
